import type { AppDispatch } from "types/store";
import type { CaseItem } from "services/cases.service";
import type { CaseDetailData } from "services/cases.service";
import { patchCaseInList, removeCaseById } from "store/casesSlice";
import { patchCaseDetailFields } from "store/caseDetailSlice";
import { cacheCaseDisplayName } from "utils/caseDisplayNameCache";

export type CaseMutationPatch = Partial<
  Pick<CaseItem, "name" | "description" | "assessment_year" | "case_curr_state" | "is_draft">
> &
  Partial<CaseDetailData>;

export function patchFromCaseStatusAction(caseStatus: string): CaseMutationPatch {
  const action = caseStatus.trim().toLowerCase().replace(/_/g, "-");
  switch (action) {
    case "submit-case":
      return { case_curr_state: "CREATED", is_draft: false };
    case "confirm-case":
      return { case_curr_state: "IN-PROGRESS" };
    case "on-hold":
      return { case_curr_state: "ON-HOLD" };
    case "mark-completed":
      return { case_curr_state: "COMPLETED" };
    case "away-from-pending":
      return { case_curr_state: "IN-PROGRESS" };
    case "pending":
      return { case_curr_state: "PENDING" };
    case "close":
      return { case_curr_state: "CLOSED" };
    case "reject-case":
    case "revert-to-draft":
    case "draft":
      return { case_curr_state: "DRAFT", is_draft: true };
    default:
      return {};
  }
}

export function syncCaseAfterMutation(
  dispatch: AppDispatch,
  caseId: string | number,
  patch: CaseMutationPatch
): void {
  if (patch.name !== undefined && patch.name !== null) {
    cacheCaseDisplayName(caseId, String(patch.name));
  }
  const listPatch: Partial<CaseItem> = {};
  if (patch.name !== undefined) listPatch.name = patch.name;
  if (patch.description !== undefined) listPatch.description = patch.description;
  if (patch.assessment_year !== undefined) listPatch.assessment_year = patch.assessment_year;
  if (patch.case_curr_state !== undefined) listPatch.case_curr_state = patch.case_curr_state;
  if (patch.is_draft !== undefined) listPatch.is_draft = patch.is_draft;
  if (Object.keys(listPatch).length > 0) {
    dispatch(patchCaseInList({ caseId, patch: listPatch }));
  }
  if (Object.keys(patch).length > 0) {
    dispatch(patchCaseDetailFields({ caseId, patch }));
  }
}

export function syncCaseStatusAfterMutation(
  dispatch: AppDispatch,
  caseId: string | number,
  caseStatus: string,
  confirmedState?: string | null
): void {
  const fromAction = patchFromCaseStatusAction(caseStatus);
  const patch: CaseMutationPatch =
    confirmedState && String(confirmedState).trim()
      ? { ...fromAction, case_curr_state: String(confirmedState).trim() }
      : fromAction;
  syncCaseAfterMutation(dispatch, caseId, patch);
}

export function removeCaseFromStore(dispatch: AppDispatch, caseId: string | number): void {
  dispatch(removeCaseById(caseId));
}
